import "./RegionTree.css";
import { useState, useEffect, useRef } from "react";
import Tree from "rc-tree";
import RegionNode from "./RegionNode";
import { XCircleblack } from "../../../../../Components/IconComponents";
import RegionRegistrationForm from "../../CreateRegion/RegionRegistrationForm";
import { findAllChildrenRegionsById, getThemeURL } from "../../../../../Services/EndPoints";
import { postDataWithBody, getData } from "../../../../../Services/Services";
import Modal from "react-bootstrap/Modal";
import RMMap from "../../RMMap/RMMap";
import { toast } from "react-toastify";

// This component displays a hierarchical tree of regions (like states, districts, blocks) with checkboxes for selection. It integrates with a map to show selected regions visually
const RegionTree = () => {
  // State variables
  const [treeData, setTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [regionTypeId, setRegionTypeId] = useState(null);
  const [actionType, setActionType] = useState("");
  const [keyVal, setKeyVal] = useState("");
  const [actionTypeHeader, setActionTypeHeader] = useState("");
  const [id, setId] = useState(null);
  const [districtId, setDistrictId] = useState("");
  const [selectedDistrictIds, setSelectedDistrictIds] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // User permissions state - only checking for edit permission
  const [userPermissions, setUserPermissions] = useState({
    canEdit: false,
  });

  // Store region data globally to access when checking
  const regionDataMap = useRef(new Map()).current;

  // Parse web permissions from user profile
  const parseUserPermissions = (webPermissions) => {
    if (!webPermissions) {
      return { canEdit: false };
    }

    const permissions = webPermissions.split(',').map(p => p.trim().toUpperCase());
    
    return {
      canEdit: permissions.includes('E'),
    };
  };

  // Fetch user profile to determine permissions
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        // Parse and set user permissions
        const permissions = parseUserPermissions(userProfile[0].web_permissions);
        setUserPermissions(permissions);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Generates tree nodes for a given parent node
  const generateTreeNodes = async (treeNode) => {
    const arr = [];
    let keySantized = null;
    let key = treeNode.props.eventKey;
    let isLastNode = false;

    if (key.indexOf("-") > -1) {
      keySantized = key.split("-");
      if (keySantized.length === 4) {
        isLastNode = true;
      }
      keySantized = keySantized[keySantized.length - 1];
    } else {
      keySantized = key;
    }

    const rgnList = await postDataWithBody(findAllChildrenRegionsById, [keySantized], {});

    rgnList.forEach(function (region) {
      let keyVal = `${key}-${region.id}`;

      // Store region data in the map with the key for later reference
      regionDataMap.set(keyVal, {
        name: region.name,
        id: region.id,
        region_type_id: region.region_type_id,
        gis_id: region.gis_id
      });

      // Also store the data with just the ID as key for easy lookup
      regionDataMap.set(region.id.toString(), {
        name: region.name,
        id: region.id,
        region_type_id: region.region_type_id,
        gis_id: region.gis_id
      });

      arr.push({
        title: (
          <RegionNode
            regionTypeId={region.region_type_id}
            isLastNode={isLastNode}
            keyVal={keyVal}
            selectedId={userPermissions.canEdit ? handleSelectedId : null} // Only pass handler if user can edit
            id={region.id}
            text={region.name}
            canEdit={userPermissions.canEdit} // Pass edit permission to RegionNode
          />
        ),
        key: keyVal,
        isLeaf: isLastNode,
        checkable: true,
      });
    });

    return arr;
  };

  // Handles selection of a region node (for edit/create) - only if user has edit permission
  const handleSelectedId = (idval, type, keyVal, regionTypeId) => {
    if (userPermissions.canEdit) {
      showModalPopup(idval, type, keyVal, regionTypeId);
    }
  };

  // Marks nodes as leaf nodes based on their level in the hierarchy
  const setLeaf = (treeData, curKey, level) => {
    const loopLeaf = (data, lev) => {
      const l = lev - 1;
      data.forEach((item) => {
        if (
          item.key.length > curKey.length
            ? item.key.indexOf(curKey) !== 0
            : curKey.indexOf(item.key) !== 0
        ) {
          return;
        }
        if (item.children) {
          loopLeaf(item.children, l);
        } else if (l < 1) {
          item.isLeaf = true;
        }
      });
    };
    loopLeaf(treeData, level + 1);
  };

  // Updates tree data with new child nodes
  const getNewTreeData = (treeData, curKey, child, level) => {
    const loop = (data) => {
      if (level < 1) return;
      data.forEach((item) => {
        if (curKey.indexOf(item.key) === 0) {
          if (item.children) {
            loop(item.children);
          } else {
            item.children = child;
          }
        }
      });
    };
    loop(treeData);
    setLeaf(treeData, curKey, level);
  };

  // Shows the modal popup for region creation/editing - only if user has edit permission
  const showModalPopup = (id, type, keyVal, regionTypeId) => {
    if (!userPermissions.canEdit) {
      toast.warning("You don't have permission to edit regions", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }

    let actionTypeStr = type === "EDIT" ? "Edit Region Information" : "Create Region";
    setRegionTypeId(regionTypeId);
    setShowPopup(true);
    setActionType(type);
    setKeyVal(keyVal);
    setActionTypeHeader(actionTypeStr);
    setId(id);
  };

  // Hides the modal popup
  const hideModalPopup = () => {
    setShowPopup(false);
  };

  // Initialize component
  useEffect(() => {
    getUserProfile(); // Fetch user permissions on component mount
    
    window.regionTreeComponent = {
      showModalPopup,
      hideModalPopup
    };

    // Add Bihar to the regionDataMap
    regionDataMap.set("52", {
      name: "Bihar",
      id: 52,
      region_type_id: null,
      gis_id: null
    });

    const timer = setTimeout(() => {
      setTreeData([{
        title: (
          <RegionNode
            regionTypeId={null}
            isLastNode={false}
            keyVal="52"
            selectedId={userPermissions.canEdit ? handleSelectedId : null}
            id={52}
            text="Bihar"
            canEdit={userPermissions.canEdit}
          />
        ),
        key: "52",
        checkable: true
      }]);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Update tree data when permissions change
  useEffect(() => {
    if (treeData.length > 0) {
      // Re-render tree nodes with updated permissions
      const updateTreeData = async () => {
        const newTreeData = [...treeData];
        // Force re-render of existing nodes by updating their titles
        const updateNodes = (nodes) => {
          return nodes.map(node => {
            if (node.key !== "52") { // Skip Bihar root node
              const regionData = regionDataMap.get(node.key);
              if (regionData) {
                return {
                  ...node,
                  title: (
                    <RegionNode
                      regionTypeId={regionData.region_type_id}
                      isLastNode={node.isLeaf}
                      keyVal={node.key}
                      selectedId={userPermissions.canEdit ? handleSelectedId : null}
                      id={regionData.id}
                      text={regionData.name}
                      canEdit={userPermissions.canEdit}
                    />
                  ),
                  children: node.children ? updateNodes(node.children) : undefined
                };
              }
            }
            return node;
          });
        };
        
        setTreeData(updateNodes(newTreeData));
      };
      
      updateTreeData();
    }
  }, [userPermissions.canEdit]);

  // Handles node selection (currently just logs)
  const onSelect = (info) => {
    console.log("selected", info);
  };

  // Handles checkbox state changes
  const onCheck = async (checkedKeys, info) => {
    setIsLoading(true);

    const node = info.node;
    const nodeKey = node.key;
    const regionData = regionDataMap.get(nodeKey);

    // Get all keys that are currently checked
    const currentCheckedKeys = Array.isArray(checkedKeys) ?
      checkedKeys :
      checkedKeys.checked || [];

    let newCheckedKeys = [...currentCheckedKeys];
    let newSelectedRegions = [...selectedRegions];
    let newSelectedDistrictIds = [...selectedDistrictIds];
    let newSelectedBlockIds = [...selectedBlockIds];

    try {
      if (regionData) {

        if (info.checked) {
          // Node is being checked
          if (!currentCheckedKeys.includes(nodeKey)) {
            newCheckedKeys.push(nodeKey);
          }

          // Handle Bihar node (special case)
          if (nodeKey === "52") {
            // First ensure we have all child data loaded
            if (!treeData.find(item => item.key === "52")?.children) {
              // If children aren't loaded yet, force load them
              const biharChildren = await generateTreeNodes({ props: { eventKey: "52" } });
              const newTreeData = [...treeData];
              getNewTreeData(newTreeData, "52", biharChildren, 2);
              setTreeData(newTreeData);

              // Now process all districts
              biharChildren.forEach(child => {
                if (!newCheckedKeys.includes(child.key)) {
                  newCheckedKeys.push(child.key);
                  const childData = regionDataMap.get(child.key);
                  if (childData?.gis_id) {
                    newSelectedDistrictIds.push(childData.gis_id);
                  }
                  if (childData) {
                    newSelectedRegions.push({
                      name: childData.name,
                      gis_id: childData.gis_id,
                      id: childData.id,
                      region_type_id: childData.region_type_id
                    });
                  }
                }
              });
            } else {
              // Children are already loaded, process them
              const biharChildren = treeData.find(item => item.key === "52")?.children || [];
              biharChildren.forEach(child => {
                if (!newCheckedKeys.includes(child.key)) {
                  newCheckedKeys.push(child.key);
                  const childData = regionDataMap.get(child.key);
                  if (childData?.gis_id) {
                    newSelectedDistrictIds.push(childData.gis_id);
                  }
                  if (childData) {
                    newSelectedRegions.push({
                      name: childData.name,
                      gis_id: childData.gis_id,
                      id: childData.id,
                      region_type_id: childData.region_type_id
                    });
                  }
                }
              });
            }

            // Clear any blocks that might be selected
            newSelectedBlockIds = [];
            newCheckedKeys = newCheckedKeys.filter(key => {
              const parts = key.split('-');
              return parts.length !== 3; // Remove block keys (format: parent-parent-id)
            });
          }

          // Handle other nodes (districts and blocks)
          if (nodeKey !== "52") {
            // If this is a block, ensure Bihar is not selected
            if (nodeKey.split('-').length === 3) { // Block key format
              newCheckedKeys = newCheckedKeys.filter(k => k !== "52");
              newSelectedRegions = newSelectedRegions.filter(r => r.id !== 52);
            }

            if (regionData.gis_id) {
              if (nodeKey.split('-').length !== 3) { // District
                newSelectedDistrictIds.push(regionData.gis_id);
              } else { // Block
                newSelectedBlockIds.push(regionData.gis_id);
              }
            }
            newSelectedRegions.push({
              name: regionData.name,
              gis_id: regionData.gis_id,
              id: regionData.id,
              region_type_id: regionData.region_type_id
            });

            // Check if all districts are now selected
            const biharNode = treeData.find(item => item.key === "52");
            const biharChildren = biharNode?.children || [];
            const allDistrictsSelected = biharChildren.every(district => {
              const districtData = regionDataMap.get(district.key);
              return districtData?.gis_id && newSelectedDistrictIds.includes(districtData.gis_id);
            });

            if (allDistrictsSelected && biharChildren.length > 0 && !newCheckedKeys.includes("52")) {
              newCheckedKeys.push("52");
              newSelectedRegions.push({
                name: "Bihar",
                gis_id: null,
                id: 52,
                region_type_id: null
              });

            }
          }
        } else {
          // Node is being unchecked
          newCheckedKeys = newCheckedKeys.filter(key => key !== nodeKey);

          if (nodeKey === "52") {
            // Remove all child districts
            const biharChildren = treeData.find(item => item.key === "52")?.children || [];
            biharChildren.forEach(child => {
              newCheckedKeys = newCheckedKeys.filter(key => key !== child.key);
              const childData = regionDataMap.get(child.key);
              if (childData?.gis_id) {
                newSelectedDistrictIds = newSelectedDistrictIds.filter(id => id !== childData.gis_id);
              }
            });
          }

          // Remove the current node's data
          if (regionData.gis_id) {
            if (nodeKey.split('-').length !== 3) { // District
              newSelectedDistrictIds = newSelectedDistrictIds.filter(id => id !== regionData.gis_id);
            } else { // Block
              newSelectedBlockIds = newSelectedBlockIds.filter(id => id !== regionData.gis_id);
            }
          }
          newSelectedRegions = newSelectedRegions.filter(region => region.id !== regionData.id);

          // If a district was unchecked, ensure Bihar is unchecked
          if (nodeKey !== "52" && nodeKey.split('-').length !== 3) {
            newCheckedKeys = newCheckedKeys.filter(k => k !== "52");
            newSelectedRegions = newSelectedRegions.filter(r => r.id !== 52);
          }
        }

        // Update all states
        setCheckedKeys(newCheckedKeys);
        setSelectedRegions(newSelectedRegions);
        setSelectedDistrictIds(newSelectedDistrictIds);
        setSelectedBlockIds(newSelectedBlockIds);
        setDistrictId(regionData?.gis_id || "");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Loads child nodes when a parent node is expanded
  const onLoadData = (treeNode) => {
    return new Promise(async (resolve) => {
      const newTreeData = [...treeData];
      let regionList = await generateTreeNodes(treeNode);
      getNewTreeData(newTreeData, treeNode.props.eventKey, regionList, 2);
      setTreeData(newTreeData);
      resolve();
    });
  };

  return (
    <div className="tree_container mt-2 d-flex srollable-hidden" style={{ height: "375pt" }}>
      <div className="col-sm-4 tree_div">
        <Tree
          onSelect={onSelect}
          onCheck={onCheck}
          loadData={onLoadData}
          treeData={treeData}
          checkable={true}
          checkedKeys={checkedKeys}
          checkStrictly={true}
          showLine={true}
        />
      </div>
      <div className="image_div map-sticky" style={{ overflow: "hidden" }}>
        <RMMap
          selectedRegions={selectedRegions}
          selectedDistrictIds={selectedDistrictIds}
          selectedBlockIds={selectedBlockIds}
          selectedDistrictId={districtId}
          isLoading={isLoading}
        />
      </div>

      {/* Only show modal if user has edit permission */}
      {showPopup && userPermissions.canEdit && (
        <Modal
          show={showPopup}
          size="lg"
          className="table__modal__popup"
          centered>
          <Modal.Header>
            <Modal.Title>{actionTypeHeader}</Modal.Title>
            <XCircleblack
              className="cursor-pointer"
              onClick={hideModalPopup}
              width="1.5em"
              height="1.5em"
            />
          </Modal.Header>
          <Modal.Body>
            <div>
              <RegionRegistrationForm
                regionTypeId={regionTypeId}
                id={id}
                keyVal={keyVal}
                actionType={actionType}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            {/* Footer content preserved from original */}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default RegionTree;